import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Scale, 
  Users, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  CreditCard,
  Zap,
  Lock,
  Globe
} from 'lucide-react';

const TermsOfService = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "15 Aralık 2024";
  const effectiveDate = "1 Ocak 2025";

  const sections = [
    {
      id: "introduction",
      title: "1. Genel Hükümler",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">
            Bu Kullanım Şartları ("Şartlar"), Beetrax Teknoloji A.Ş. ("Beetrax", "biz", "bizim") 
            tarafından sunulan iş takip ve stok yönetimi platformunun kullanımını düzenler.
          </p>
          
          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'} mb-4`}>
            <div className="flex items-start">
              <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h4 className={`font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'} mb-1`}>
                  Şartların Kabulü
                </h4>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  Beetrax platformunu kullanarak bu şartları kabul etmiş sayılırsınız.
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-medium mb-3">1.1 Uygulama Alanı</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>www.beetrax.com web sitesi</li>
            <li>Beetrax mobil uygulamaları</li>
            <li>API hizmetleri ve entegrasyonlar</li>
            <li>Müşteri destek hizmetleri</li>
          </ul>

          <h4 className="font-medium mb-3">1.2 Tanımlar</h4>
          <div className="space-y-2">
            <p><strong>Platform:</strong> Beetrax tarafından sunulan tüm hizmetler</p>
            <p><strong>Kullanıcı:</strong> Platformu kullanan gerçek veya tüzel kişiler</p>
            <p><strong>İçerik:</strong> Platform üzerinde yer alan tüm veriler ve bilgiler</p>
            <p><strong>Hesap:</strong> Kullanıcının platform üzerindeki üyelik hesabı</p>
          </div>
        </div>
      )
    },
    {
      id: "user-accounts",
      title: "2. Kullanıcı Hesapları",
      icon: <Users className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">2.1 Hesap Oluşturma</h4>
          <p className="mb-4">
            Beetrax platformunu kullanmak için geçerli bir hesap oluşturmanız gerekmektedir. 
            Hesap oluşturırken verdiğiniz bilgilerin doğru ve güncel olması zorunludur.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <div className="flex items-center mb-2">
                <CheckCircle className={`w-4 h-4 mr-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <h5 className="font-medium">Yapabilecekleriniz</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Birden fazla proje oluşturmak</li>
                <li>• Ekip üyeleri davet etmek</li>
                <li>• Verileri dışa aktarmak</li>
                <li>• API kullanmak</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-50'} border ${isDark ? 'border-red-700' : 'border-red-200'}`}>
              <div className="flex items-center mb-2">
                <XCircle className={`w-4 h-4 mr-2 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <h5 className="font-medium">Yasaklananlar</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Sahte bilgiler vermek</li>
                <li>• Hesabı başkasıyla paylaşmak</li>
                <li>• Otomatik hesap oluşturmak</li>
                <li>• Sistem güvenliğini tehdit etmek</li>
              </ul>
            </div>
          </div>

          <h4 className="font-medium mb-3">2.2 Hesap Güvenliği</h4>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'} border ${isDark ? 'border-yellow-700' : 'border-yellow-200'} mb-4`}>
            <div className="flex items-start">
              <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div>
                <h5 className={`font-medium ${isDark ? 'text-yellow-200' : 'text-yellow-800'} mb-1`}>
                  Güvenlik Sorumluluğu
                </h5>
                <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  Hesabınızın güvenliğini sağlamak sizin sorumluluğunuzdadır. Güçlü şifreler kullanın ve hesap bilgilerinizi kimseyle paylaşmayın.
                </p>
              </div>
            </div>
          </div>

          <ul className="list-disc pl-6 space-y-2">
            <li>En az 8 karakter uzunluğunda güçlü şifre kullanın</li>
            <li>İki faktörlü kimlik doğrulamayı aktif edin</li>
            <li>Hesap bilgilerinizi güncel tutun</li>
            <li>Şüpheli aktiviteleri derhal bildirin</li>
          </ul>
        </div>
      )
    },
    {
      id: "platform-usage",
      title: "3. Platform Kullanımı",
      icon: <Zap className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">3.1 Hizmet Sınırları</h4>
          <p className="mb-4">
            Beetrax platformu, seçtiğiniz pakete göre belirli sınırlar dahilinde hizmet sunar. 
            Bu sınırlar proje sayısı, kullanıcı sayısı ve depolama alanı gibi faktörleri içerir.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center mb-2">
                <Users className="w-4 h-4 mr-2" />
                <h5 className="font-medium text-sm">Başlangıç Paketi</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• 5 kullanıcıya kadar</li>
                <li>• 10 proje</li>
                <li>• 5GB depolama</li>
                <li>• E-posta desteği</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
              <div className="flex items-center mb-2">
                <Zap className="w-4 h-4 mr-2" />
                <h5 className="font-medium text-sm">Profesyonel</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• 25 kullanıcıya kadar</li>
                <li>• Sınırsız proje</li>
                <li>• 50GB depolama</li>
                <li>• Öncelikli destek</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'} border ${isDark ? 'border-purple-700' : 'border-purple-200'}`}>
              <div className="flex items-center mb-2">
                <Globe className="w-4 h-4 mr-2" />
                <h5 className="font-medium text-sm">Kurumsal</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Sınırsız kullanıcı</li>
                <li>• Özel özellikler</li>
                <li>• 500GB+ depolama</li>
                <li>• 7/24 destek</li>
              </ul>
            </div>
          </div>

          <h4 className="font-medium mb-3">3.2 Kabul Edilebilir Kullanım</h4>
          <p className="mb-3">Platformu aşağıdaki amaçlarla kullanamazsınız:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Yasadışı faaliyetler veya zararlı içerik paylaşımı</li>
            <li>Diğer kullanıcıların haklarını ihlal etmek</li>
            <li>Spam veya istenmeyen içerik göndermek</li>
            <li>Sistem güvenliğini test etmek veya zarar vermek</li>
            <li>Fikri mülkiyet haklarını ihlal etmek</li>
          </ul>
        </div>
      )
    },
    {
      id: "payments",
      title: "4. Ödeme ve Abonelik",
      icon: <CreditCard className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">4.1 Ücretlendirme</h4>
          <p className="mb-4">
            Beetrax freemium model ile hizmet sunar. Temel özellikler ücretsizdir, 
            gelişmiş özellikler için abonelik gereklidir.
          </p>

          <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'} mb-4`}>
            <div className="flex items-start">
              <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <div>
                <h5 className={`font-medium ${isDark ? 'text-green-200' : 'text-green-800'} mb-1`}>
                  Ücretsiz Deneme
                </h5>
                <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                  14 gün boyunca tüm pro özellikleri ücretsiz deneyebilirsiniz.
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-medium mb-3">4.2 Ödeme Koşulları</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Ödemeler aylık veya yıllık olarak yapılabilir</li>
            <li>Yıllık ödemelerde %20 indirim uygulanır</li>
            <li>Tüm fiyatlar KDV dahildir</li>
            <li>Ödeme yöntemleri: Kredi kartı, banka havalesi</li>
          </ul>

          <h4 className="font-medium mb-3">4.3 İptal ve İade</h4>
          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">💳 İade Politikası</h5>
              <p className="text-sm">7 gün içinde koşulsuz iade hakkınız vardır.</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">📅 Abonelik İptali</h5>
              <p className="text-sm">Aboneliğinizi istediğiniz zaman iptal edebilirsiniz.</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">💾 Veri Saklama</h5>
              <p className="text-sm">İptal sonrası verileriniz 30 gün daha saklanır.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "intellectual-property",
      title: "5. Fikri Mülkiyet Hakları",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">5.1 Beetrax'ın Hakları</h4>
          <p className="mb-4">
            Beetrax platformu, kaynak kodu, tasarım, marka ve tüm fikri mülkiyet hakları 
            Beetrax Teknoloji A.Ş.'ye aittir.
          </p>

          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-50'} border ${isDark ? 'border-red-700' : 'border-red-200'} mb-4`}>
            <div className="flex items-start">
              <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              <div>
                <h5 className={`font-medium ${isDark ? 'text-red-200' : 'text-red-800'} mb-1`}>
                  Uyarı: Kopyalama Yasaktır
                </h5>
                <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                  Platform kodlarını kopyalamak, tersine mühendislik yapmak veya benzer ürün geliştirmek yasaktır.
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-medium mb-3">5.2 Kullanıcı İçeriği</h4>
          <p className="mb-3">Platform üzerinde oluşturduğunuz içeriklerle ilgili:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>İçeriklerinizin sahibi sizsiniz</li>
            <li>Beetrax'a platform üzerinde kullanım lisansı verirsiniz</li>
            <li>İçeriklerin yasal olmasından siz sorumlusunuz</li>
            <li>Başkalarının haklarını ihlal etmemelisiniz</li>
          </ul>

          <h4 className="font-medium mb-3">5.3 Üçüncü Taraf İçerikleri</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h5 className="font-medium mb-2">📚 Açık Kaynak</h5>
              <p className="text-sm">Kullandığımız açık kaynak kütüphanelerin lisanslarını açıklıyoruz.</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h5 className="font-medium mb-2">🔗 Entegrasyonlar</h5>
              <p className="text-sm">Üçüncü taraf hizmetlerin kendi şartları geçerlidir.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "privacy-security",
      title: "6. Gizlilik ve Güvenlik",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">6.1 Veri Koruma</h4>
          <p className="mb-4">
            Verilerinizin güvenliği bizim için önceliklidir. KVKK ve GDPR standartlarına 
            uygun şekilde verilerinizi koruyoruz.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
              <div className="flex items-center mb-2">
                <Lock className="w-4 h-4 mr-2" />
                <h5 className="font-medium">Teknik Güvenlik</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• 256-bit SSL şifreleme</li>
                <li>• Düzenli güvenlik testleri</li>
                <li>• Güvenlik duvarları</li>
                <li>• Veri yedekleme</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <div className="flex items-center mb-2">
                <CheckCircle className="w-4 h-4 mr-2" />
                <h5 className="font-medium">Yasal Uyumluluk</h5>
              </div>
              <ul className="text-sm space-y-1">
                <li>• KVKK uyumlu</li>
                <li>• GDPR standartları</li>
                <li>• ISO 27001 sertifikası</li>
                <li>• Düzenli denetimler</li>
              </ul>
            </div>
          </div>

          <h4 className="font-medium mb-3">6.2 Kullanıcı Sorumlulukları</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Hesap bilgilerinizi güvenli tutun</li>
            <li>Şüpheli aktiviteleri bildirin</li>
            <li>Güvenlik güncellemelerini takip edin</li>
            <li>Güçlü şifreler kullanın</li>
          </ul>
        </div>
      )
    },
    {
      id: "limitation-liability",
      title: "7. Sorumluluk Sınırları",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">7.1 Hizmet Garantileri</h4>
          <p className="mb-4">
            Beetrax platformunu "olduğu gibi" sunuyoruz. %99.9 uptime hedefliyoruz ancak 
            kesintisiz hizmet garantisi veremiyoruz.
          </p>

          <div className={`p-4 rounded-lg ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'} border ${isDark ? 'border-yellow-700' : 'border-yellow-200'} mb-4`}>
            <div className="flex items-start">
              <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div>
                <h5 className={`font-medium ${isDark ? 'text-yellow-200' : 'text-yellow-800'} mb-1`}>
                  Önemli Uyarı
                </h5>
                <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  Verilerinizi düzenli olarak yedeklemenizi öneririz. Veri kaybından sorumlu olmayabiliriz.
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-medium mb-3">7.2 Sorumluluk Sınırları</h4>
          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">💰 Mali Sorumluluk</h5>
              <p className="text-sm">Toplam sorumluluğumuz ödediğiniz yıllık ücreti aşamaz.</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">⚠️ Dolaylı Zararlar</h5>
              <p className="text-sm">Kayıp kar, iş kaybı gibi dolaylı zararlardan sorumlu değiliz.</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h5 className="font-medium mb-1">🔗 Üçüncü Taraf Hizmetleri</h5>
              <p className="text-sm">Entegre hizmetlerin sorunlarından sorumlu olmayabiliriz.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "termination",
      title: "8. Sözleşme Sona Ermesi",
      icon: <XCircle className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">8.1 Hesap Kapatma</h4>
          <p className="mb-4">
            Hesabınızı istediğiniz zaman kapatabilirsiniz. Ayrıca şartları ihlal etmeniz 
            durumunda hesabınızı askıya alabilir veya kapatabiliz.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h5 className="font-medium mb-2">✅ Gönüllü Kapatma</h5>
              <ul className="text-sm space-y-1">
                <li>• İstediğiniz zaman yapabilirsiniz</li>
                <li>• Verilerinizi indirebilirsiniz</li>
                <li>• 30 gün veri saklama</li>
                <li>• Kalan ücret iadesi</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-50'} border ${isDark ? 'border-red-700' : 'border-red-200'}`}>
              <h5 className="font-medium mb-2">⚠️ Zorunlu Kapatma</h5>
              <ul className="text-sm space-y-1">
                <li>• Şart ihlalleri durumunda</li>
                <li>• Ödeme gecikmeleri</li>
                <li>• Yasal gereklilikler</li>
                <li>• Güvenlik tehditleri</li>
              </ul>
            </div>
          </div>

          <h4 className="font-medium mb-3">8.2 Kapatma Sonrası</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Verilerinize 30 gün boyunca erişebilirsiniz</li>
            <li>30 gün sonra veriler kalıcı olarak silinir</li>
            <li>API erişimi derhal sonlanır</li>
            <li>Kalan ücretten iade alabilirsiniz</li>
          </ul>
        </div>
      )
    },
    {
      id: "legal-compliance",
      title: "9. Yasal Hükümler",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">9.1 Geçerli Hukuk</h4>
          <p className="mb-4">
            Bu sözleşme Türkiye Cumhuriyeti kanunlarına tabidir. İhtilaflar İstanbul 
            mahkemelerinde görülür.
          </p>

          <h4 className="font-medium mb-3">9.2 Şart Değişiklikleri</h4>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'} mb-4`}>
            <div className="flex items-start">
              <Clock className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h5 className={`font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'} mb-1`}>
                  Bildirim Süreci
                </h5>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  Önemli değişiklikler 30 gün önceden e-posta ile bildirilir.
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-medium mb-3">9.3 İletişim Bilgileri</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center mb-3">
                <Mail className="w-5 h-5 mr-2" />
                <h5 className="font-medium">E-posta İletişim</h5>
              </div>
              <p className="text-sm mb-1">Genel:</p>
              <p className="text-sm text-blue-600 mb-2">info@beetrax.net</p>
              <p className="text-sm mb-1">Yasal:</p>
              <p className="text-sm text-blue-600">info@beetrax.net</p>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center mb-3">
                <Phone className="w-5 h-5 mr-2" />
                <h5 className="font-medium">Telefon Desteği</h5>
              </div>
              <p className="text-sm mb-1">Destek Hattı:</p>
              <p className="text-sm text-blue-600 mb-2">+90 (552) 424 11 08</p>
              <p className="text-sm mb-1">Çalışma Saatleri:</p>
              <p className="text-sm">09:00 - 18:00 (Hafta içi)</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <FileText className={`w-7 h-7 mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h1 className="text-2xl font-bold">Kullanım Şartları</h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Son güncelleme: {lastUpdated} | Yürürlük: {effectiveDate}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
            }`}>
              v2.1
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Introduction Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-start">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'} mr-4`}>
                <Scale className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Beetrax Kullanım Sözleşmesi</h2>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  Bu kullanım şartları, Beetrax platformunu güvenli ve verimli şekilde kullanmanız için 
                  gerekli kuralları ve koşulları belirler. Lütfen dikkatli bir şekilde okuyun.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <h3 className="text-lg font-semibold mb-4">İçindekiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={`flex items-center p-3 rounded-lg ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  <span className={`mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {section.icon}
                  </span>
                  <span className="text-sm font-medium">{section.title}</span>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Content Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 2) }}
                className={`${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border rounded-xl p-6 shadow-sm`}
              >
                <div className="flex items-center mb-4">
                  <span className={`mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {section.icon}
                  </span>
                  <h2 className="text-xl font-bold">{section.title}</h2>
                </div>
                <div className={`${isDark ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {section.content}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className={`mt-12 p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border text-center`}
          >
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Bu kullanım şartları {lastUpdated} tarihinde güncellenmiş olup {effectiveDate} tarihinde yürürlüğe girecektir.
              Sorularınız için legal@beetrax.com adresine yazabilirsiniz.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/privacy-policy')}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                } transition-colors`}
              >
                Gizlilik Politikası
              </button>
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-2 rounded-lg ${
                  isDark 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } transition-colors`}
              >
                Ana Sayfaya Dön
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 